import React, { FormEvent, useEffect, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, Checkbox, FormControlLabel, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { useConfirm } from 'material-ui-confirm';

import { IExpense } from '../../types/expense';
import { IFuzzySearchResult } from '../../types/system';
import { ISelectOption } from '../../types/list';

import { useListContext } from '../../contexts/listContext';
import { useUserContext } from '../../contexts/userContext';
import { AppConfig } from '../../config/appConfig';

import { ErrorProvider } from '../../classes/errorProvider';
import { CustomerService } from '../../services/customerService';
import { ExpenseService } from '../../services/expenseService';
import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';

import '../../styles/expenseEditForm.scss';
import { VendorService } from '../../services/vendorService';
import { IVendorLight } from '../../types/vendor';

export interface IExpenseEditForm {
	action: "add" | "edit";
	expense: IExpense;
	reloadData: () => void;
}

const ExpenseEditForm: React.FunctionComponent<IExpenseEditForm> = ({ action, expense, reloadData }) => {
	const confirm = useConfirm();
	const listContext = useListContext();
	const userContext = useUserContext();
	const navigate = useNavigate();
	const [formFields, setFormFields] = useState<IExpense>(expense);
	const [originalExpenseName, setOriginalExpenseName] = useState<string>(expense?.name);
	const [vendorsOptions, setVendorOptions] = useState<ISelectOption[]>([]);
	const [isLoadingVendorOptions, setIsLoadingVendorOptions] = useState<boolean>(true);
	const [isSaving, setIsSaving] = useState(false);
	const [isCheckingNameForDupes, setIsCheckingNameForDupes] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [successMessage, setSuccessMessage] = useState<string | undefined>();

	const processRequiredMessage = "Process is required.";
	const processOptions: ISelectOption[] = [{ name: "Industrial", value: "IN" }, { name: "Production", value: "PR" }, { name: "Special Project", value: "SP" }];

	useEffect(() => {
		if (action == "edit") {
			getVendors();
		}
		
	},[])

	const getVendors = () => {
		VendorService.getActiveVendors()
			.then((response) => {
				console.log('VendorService.getActiveVendors: SUCCESS', response);
				const options: ISelectOption[] = response.data.map((v: IVendorLight) => { return { value: v.id, name: v.name, isActive: v.status == "Active" } as ISelectOption });
				options.sort((a, b) => UtilityService.compareStrings(a.name, b.name));
				setVendorOptions(options);
			})
			.catch((error) => {
				console.log('VendorService.getActiveVendors: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsLoadingVendorOptions(false);
			});
	}

	const doSave = async () => {
		clearSaveResult();
		
		const payload: IExpense = { ...formFields };
		if (payload.process == "-1") {
			payload.process = undefined;
		}

		if (payload.process == undefined) {
			setErrorMessage(processRequiredMessage);
			return;
		}

		setIsSaving(true);

		if (action == "add") {
			ExpenseService.addExpense(payload)
				.then((response) => {
					console.log('ExpenseService.addExpense: SUCCESS', response);
					setSuccessMessage("Expense has been added.");
					setTimeout(() => {
						navigate("/expense/" + response.data.id);
					}, AppConfig.addSuccessToastMs);
				})
				.catch((error) => {
					console.log('ExpenseService.addExpense: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsSaving(false);
					window.scroll(0, 0);
				});
		}
		else if (action == "edit" && expense?.id) {
			ExpenseService.updateExpense(payload)
				.then((response) => {
					console.log('ExpenseService.updateExpense: SUCCESS', response);
					reloadData();
					setSuccessMessage("Expense details have been saved.");
					setTimeout(() => {
						setSuccessMessage(undefined);
					}, AppConfig.editSuccessToastMs);
				})
				.catch((error) => {
					console.log('ExpenseService.updateExpense: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsSaving(false);
					window.scrollTo(0, 0);
				});
		}
	}

	const handleChangeCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
		const name = event.target.name;
		const value = event.target.checked;
		setFormFields({ ...formFields, [name]: value });
	};

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		// Fuzzy check existing customer names for potential duplicates before saving		
		event.preventDefault();
		setErrorMessage(undefined);

		// Skip fuzzy dupe checking for now
		await doSave();
		return;

		// If the name wasn't changed then don't bother checking for duplicates
		if (action == "edit" && formFields?.name == originalExpenseName) {
			await doSave();
			return;
		}

		setIsCheckingNameForDupes(true);

		CustomerService.fuzzySearchForDuplicateCompanyNames({ name: formFields?.name, excludeId: action == "edit" ? +expense.id : undefined })
			.then(async (response) => {
				console.log('CustomerService.fuzzySearchForDuplicateCompanyNames: SUCCESS', response);
				const matches: IFuzzySearchResult[] = response.data;
				if (matches.length > 0) {
					if (await UtilityService.ConfirmationModal(
						confirm,
						<div className="dupes-dialog-title">
							<Icons.Warning fill="#272f50" height="14px"></Icons.Warning>
							Potential Duplicates
						</div>,
						<div className="dupes-dialog-content">
							The name <strong>{formFields.name}</strong> is similar to <strong>{matches.length}</strong> existing expense{matches.length > 1 ? "s" : ""}. Please review the conflicts below before proceeding.<br />
							<ul>
								{matches.map((item, index) => (
									<li key={index}>
										<a href={`/customer/${item.id}`} target="_blank" rel="noopener noreferrer">
											<Icons.ViewItem height="14px"></Icons.ViewItem>
											{item.name}
										</a>
									</li>
								))}
							</ul>
						</div>,
						{
							confirmationText: "Continue",
							confirmationButtonProps: { color: 'primary', variant: 'contained' },
							cancellationText: "Cancel"
						}
					) == false) {
						return;
					}
				}

				await doSave();
			})
			.catch((error) => {
				console.log('CustomerService.fuzzySearchForDuplicateCompanyNames: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsCheckingNameForDupes(false);
			});
	}

	const clearSaveResult = () => {
		setErrorMessage(undefined);
		setSuccessMessage(undefined);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<section className="hwi-expense-edit-form">
				{successMessage &&
					<Alert className="alert-success"
						severity="success"
						action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
						{successMessage}
					</Alert>
				}
				{errorMessage &&
					<Alert className="alert-error"
						severity="error"
						action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
						{errorMessage}
					</Alert>
				}
				<Form onSubmit={(event) => handleSubmit(event)}>
					<div className="row">
						<InputLabel>Expense Name *</InputLabel>
						<TextField className="name-field" required name="name" value={formFields.name} size="small" onChange={(event) => { setFormFields({ ...formFields, ["name"]: event.target.value }) }} inputProps={{ maxLength: 50 }}></TextField>
					</div>
					<div className="row">
						<InputLabel>Category *</InputLabel>
						<TextField className="category-field" required name="category" value={formFields.category} size="small" onChange={(event) => { setFormFields({ ...formFields, ["category"]: event.target.value }) }} inputProps={{ maxLength: 50 }}></TextField>
					</div>
					<div className="row">
						<InputLabel>Process *</InputLabel>
						<Select
							value={formFields.process || "-1"}
							className={`process-field${errorMessage === processRequiredMessage ? " error" : ""}`}
							required
							onChange={(event) => { setFormFields({ ...formFields, ["process"]: event.target.value }) }}
						>
							<MenuItem value="-1" key="-1">Select a process</MenuItem>
							{processOptions.map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>	
					<div className="row">
						<FormControlLabel className="spread-across-materials-field" control={<Checkbox name="spreadAcrossMaterials" checked={formFields.spreadAcrossMaterials} onChange={handleChangeCheck}></Checkbox>} label="Distribute this Cost" />
					</div>				
					<div className="row">
						<FormControlLabel className="active-field" control={<Checkbox name="isActive" checked={formFields.isActive} onChange={handleChangeCheck}></Checkbox>} label="Active" />
					</div>
					<div className="row controls">
						<Button type="submit" className="save-button" disabled={isCheckingNameForDupes || isSaving} variant="contained">{isCheckingNameForDupes || isSaving ? "Saving..." : "Save Details"}</Button>
					</div>
				</Form>
				{/* <Json data={formFields}></Json> */}
			</section>
		</LocalizationProvider >
	)
}

export default ExpenseEditForm;